import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eraser, Loader2, PenLine, Save, Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import SignaturePadCanvas, { type SignaturePadHandle } from '@/components/agreements/SignaturePadCanvas';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/logger';
import { SIGNATURE_BUCKET, parseAgreementError, signatureObjectUrl } from '@/lib/agreements/constants';

/**
 * Settings — currently one thing, and that one thing is load-bearing.
 *
 * A COMPANY COUNTERSIGNATURE IS SIGNED WITH THE SIGNATURE SAVED HERE. `agreement_sign`
 * accepts a signature object only if it exists AND this session uploaded it, and the
 * bucket's INSERT policy only admits `admins/<own profile id>.png`, so the path below is
 * the one path a countersignature can come from. Saving it is therefore a prerequisite for
 * signing on behalf of the company, not a personalisation.
 */
export default function Settings() {
    const queryClient = useQueryClient();
    const padRef = useRef<SignaturePadHandle>(null);
    const [hasInk, setHasInk] = useState(false);

    const { data: profile, isLoading } = useQuery({
        queryKey: ['my-signing-profile'],
        queryFn: async () => {
            const { data: auth } = await supabase.auth.getUser();
            if (!auth?.user) return null;
            const { data, error } = await (supabase as any)
                .from('profiles')
                .select('id, first_name, last_name, email, signature_path, admins(role)')
                .eq('user_id', auth.user.id)
                .maybeSingle();
            if (error) throw error;
            return data;
        },
        staleTime: 60_000,
    });

    const savedUrl = useSignedUrl(profile?.signature_path ? signatureObjectUrl(profile.signature_path) : null);

    const save = useMutation({
        mutationFn: async () => {
            const blob = await padRef.current?.toPngBlob();
            if (!blob) throw new Error('Draw your signature before saving');
            if (!profile?.id) throw new Error('No profile found for this account');

            // The path is fixed by the bucket policy: `admins/<own profile id>.png` is the
            // only object under `admins/` this session is allowed to write.
            const path = `admins/${profile.id}.png`;

            const { error: uploadError } = await supabase.storage
                .from(SIGNATURE_BUCKET)
                .upload(path, blob, { contentType: 'image/png', upsert: true });
            if (uploadError) throw uploadError;

            const { error } = await (supabase as any)
                .from('profiles')
                .update({ signature_path: path })
                .eq('id', profile.id);
            if (error) throw error;

            return path;
        },
        onSuccess: () => {
            toast.success('Signature saved');
            padRef.current?.clear();
            setHasInk(false);
            queryClient.invalidateQueries({ queryKey: ['my-signing-profile'] });
        },
        onError: (error) => {
            captureError(error);
            toast.error(parseAgreementError(error).message);
        },
    });

    const signerName = profile
        ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim()
        : '';
    const signerRole = Array.isArray(profile?.admins)
        ? profile?.admins[0]?.role
        : profile?.admins?.role;

    return (
        <div className="animate-fade-in space-y-6 px-4 pt-6 sm:px-6 lg:px-8">
            <div className="mb-8">
                <div className="mb-2 flex items-center gap-3">
                    <SettingsIcon className="h-8 w-8" />
                    <h1 className="text-4xl font-semibold tracking-tight">Settings</h1>
                </div>
                <p className="text-muted-foreground">Your account and signing preferences.</p>
            </div>

            <Card className="max-w-3xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <PenLine className="h-5 w-5" />
                        My signature
                    </CardTitle>
                    <CardDescription>
                        Drawn once and reused every time you countersign an agreement on behalf of Jpad Prime
                        Lane Ltd. It is stored privately and appears only on documents you sign.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {isLoading ? (
                        <Skeleton className="h-40 w-full" />
                    ) : (
                        <>
                            <div className="rounded-lg border bg-muted/40 p-4">
                                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    Signatory
                                </p>
                                <p className="mt-1 font-medium">{signerName || profile?.email || '—'}</p>
                                {signerRole && <p className="text-sm text-muted-foreground">{signerRole}</p>}
                            </div>

                            <div>
                                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    Saved signature
                                </p>
                                {profile?.signature_path ? (
                                    <div className="flex h-28 items-center justify-center rounded-lg border bg-background p-3">
                                        {savedUrl ? (
                                            <img
                                                src={savedUrl}
                                                alt={`Saved signature of ${signerName || 'this account'}`}
                                                className="max-h-24 max-w-full object-contain dark:invert"
                                            />
                                        ) : (
                                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                        )}
                                    </div>
                                ) : (
                                    <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                                        No signature saved yet. Until you save one you cannot countersign an
                                        agreement.
                                    </p>
                                )}
                            </div>

                            <div>
                                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    {profile?.signature_path ? 'Draw a replacement' : 'Draw your signature'}
                                </p>
                                <SignaturePadCanvas ref={padRef} onInkChange={setHasInk} />
                                {profile?.signature_path && (
                                    <p className="mt-2 text-xs text-muted-foreground">
                                        A replacement is saved over the same file, so it also becomes
                                        the ink on any document you have already countersigned that
                                        has not been finalized yet — the executed PDF is rendered from
                                        your saved signature when the last party signs. Documents
                                        already finalized keep the signature they were issued with.
                                    </p>
                                )}
                                <div className="mt-3 flex justify-end gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            padRef.current?.clear();
                                            setHasInk(false);
                                        }}
                                        disabled={!hasInk}
                                    >
                                        <Eraser className="mr-2 h-4 w-4" />
                                        Clear
                                    </Button>
                                    <Button onClick={() => save.mutate()} disabled={!hasInk || save.isPending}>
                                        {save.isPending ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                            <Save className="mr-2 h-4 w-4" />
                                        )}
                                        Save signature
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
